import { Star, ExternalLink, CheckCircle, Calendar, FileText } from 'lucide-react';

function sigStyle(sig) {
  const s = sig?.toLowerCase() || '';
  if (s === 'pathogenic') return { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5', dot: '#ef4444' };
  if (s === 'likely pathogenic') return { bg: '#fff7ed', text: '#9a3412', border: '#fdba74', dot: '#f97316' };
  if (s === 'uncertain significance' || s === 'vus') return { bg: '#fafafa', text: '#374151', border: '#d1d5db', dot: '#9ca3af' };
  if (s === 'likely benign') return { bg: '#f0fdf4', text: '#166534', border: '#86efac', dot: '#22c55e' };
  if (s === 'benign') return { bg: '#f0fdf4', text: '#14532d', border: '#4ade80', dot: '#16a34a' };
  return { bg: '#f8fafc', text: '#475569', border: '#cbd5e1', dot: '#94a3b8' };
}

function StarRating({ count, max = 4 }) {
  return (
    <div className="flex items-center gap-0.5" title={`${count} of ${max} review stars`}>
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          size={16}
          fill={i < count ? '#f59e0b' : 'none'}
          className={i < count ? 'star-filled' : 'star-empty'}
        />
      ))}
    </div>
  );
}

const STAR_LABELS = [
  '',
  'Single submitter',
  'Multiple submitters, no conflict',
  'Expert panel reviewed',
  'Practice guideline',
];

export default function ClinVarSection({ clinvar }) {
  const c = sigStyle(clinvar.significance);
  const submittedDate = new Date(clinvar.last_evaluated).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <section id="clinvar" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">ClinVar</h3>
        </div>
        <a
          href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${clinvar.variation_id}/`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
        >
          {clinvar.id}
          <ExternalLink size={12} />
        </a>
      </div>

      <div className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: significance + review */}
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-xs text-gray-500 font-medium mb-2">Clinical Significance</div>
              <div
                className="inline-flex items-center gap-2.5 px-4 py-3 rounded-xl border"
                style={{ background: c.bg, borderColor: c.border }}
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: c.dot }} />
                <span className="font-bold text-lg" style={{ color: c.text }}>{clinvar.significance}</span>
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500 font-medium mb-2">Review Status</div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
                <StarRating count={clinvar.review_stars} />
                <div>
                  <div className="text-sm font-semibold text-gray-800 capitalize">{clinvar.review_status}</div>
                  <div className="text-xs text-gray-500">{STAR_LABELS[clinvar.review_stars]}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Submissions</div>
                <div className="font-bold text-gray-900 text-xl">{clinvar.submissions}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Last Evaluated</div>
                <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                  <Calendar size={14} className="text-gray-400" />
                  {submittedDate}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Allele ID</div>
                <div className="font-mono font-semibold text-gray-700">{clinvar.allele_id}</div>
              </div>
            </div>
          </div>

          {/* Right: conditions */}
          <div>
            <div className="text-xs text-gray-500 font-medium mb-3">Associated Conditions</div>
            <div className="flex flex-col gap-2.5">
              {clinvar.conditions.map((cond, i) => (
                <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-800">{cond.name}</div>
                    <a
                      href={`https://www.ncbi.nlm.nih.gov/medgen/${cond.medgen}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline font-mono flex items-center gap-1 mt-0.5"
                    >
                      MedGen: {cond.medgen}
                      <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${clinvar.variation_id}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110 text-white"
              style={{ background: '#021f30' }}
            >
              View on ClinVar
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
